/**
 * Canvas-based PDF renderer with continuous scrolling.
 *
 * UX
 *  - Renders every page stacked vertically so the reader can scroll
 *    naturally (matches the DOCX/long-document mental model). Sticky-
 *    toolbar arrows in the parent are page-up / page-down equivalents
 *    that smooth-scroll the chosen page into view.
 *  - Reports the currently visible page back to the parent via
 *    `onPageChange` so the "{n} / {total}" indicator stays in sync
 *    while the reader scrolls.
 *
 * Performance
 *  - Pages render lazily as they approach the viewport (rootMargin
 *    of one viewport). Placeholders preserve the scrollbar height so
 *    the scroll target stays stable. Once rendered, canvases are kept
 *    cached (PDFs are typically <200 pages of low-res reading text).
 *
 * Security (unchanged from previous canvas viewer)
 *  - No iframe / blob URL / <a download> — bytes never become a file
 *    URL outside the worker.
 *  - oncontextmenu suppressed; draggable=false on every canvas.
 *  - ArrayBuffer cloned before handing to pdfjs (pdfjs detaches it).
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as pdfjsLib from "pdfjs-dist";
import { resolveAssetUrl } from "@/lib/assetLoader";

// Use CDN-hosted worker matching the exact installed pdfjs-dist version.
// Avoids production MIME-type / build-output issues with .mjs files behind nginx.
const CDN_WORKER = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
// Self-hosted fallback served from CRA's /public folder. We deliberately use
// the .js extension (not .mjs) so the default nginx mime.types maps it to
// `application/javascript` and the browser accepts the dynamic module import.
const LOCAL_WORKER = "/pdf.worker.min.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = CDN_WORKER;

// Probe CDN once on module load; swap to the self-hosted worker if blocked.
if (typeof window !== "undefined") {
  resolveAssetUrl("pdf-worker", CDN_WORKER, LOCAL_WORKER).then((src) => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = src;
  });
}

const PdfCanvasViewer = forwardRef(function PdfCanvasViewer(
  { data, onLoaded, onPageChange, onError, zoom = 1 },
  ref
) {
  const wrapRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  // Aspect ratio used for placeholder height before a page renders.
  // PDFs commonly have a single trim size, so page-1's ratio is a
  // good-enough heuristic and avoids prefetching every page up front.
  const [aspect, setAspect] = useState(11 / 8.5);

  // pageNum → { wrapEl, canvasEl, rendered, renderTask }
  const pageRegistry = useRef({});

  // Imperative API used by the parent's prev/next chevrons.
  useImperativeHandle(
    ref,
    () => ({
      scrollToPage(n) {
        const entry = pageRegistry.current[n];
        if (entry?.wrapEl) {
          entry.wrapEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      },
    }),
    []
  );

  // Open the document whenever `data` (ArrayBuffer) changes.
  useEffect(() => {
    let cancelled = false;
    if (!data) return undefined;
    let loaded = null;
    (async () => {
      try {
        // Clone — pdf.js transfers ownership to its worker, so a fresh
        // copy is required on each load (StrictMode remount, parent
        // updates, etc.).
        const bytes = new Uint8Array(data.byteLength);
        bytes.set(new Uint8Array(data));
        const loadingTask = pdfjsLib.getDocument({
          data: bytes,
          disableAutoFetch: true,
          disableStream: true,
        });
        loaded = await loadingTask.promise;
        if (cancelled) {
          loaded.destroy?.();
          return;
        }
        setPdf(loaded);
        setNumPages(loaded.numPages);
        // Aspect ratio for placeholders — read page 1 once.
        try {
          const first = await loaded.getPage(1);
          const v = first.getViewport({ scale: 1 });
          if (!cancelled) setAspect(v.height / v.width);
        } catch {
          /* fall back to default aspect */
        }
        onLoaded?.({ totalPages: loaded.numPages });
      } catch (e) {
        if (!cancelled) onError?.(e);
      }
    })();
    return () => {
      cancelled = true;
      // Cancel any in-flight renders before tearing down the pdf doc.
      for (const entry of Object.values(pageRegistry.current)) {
        try { entry.renderTask?.cancel?.(); } catch { /* ignore */ }
      }
      pageRegistry.current = {};
      loaded?.destroy?.();
      setPdf(null);
      setNumPages(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Render a single page into its canvas. Idempotent — bails out if
  // the canvas was already rendered or no longer exists.
  const renderPage = useCallback(
    async (pageNum) => {
      if (!pdf) return;
      const entry = pageRegistry.current[pageNum];
      if (!entry || entry.rendered || !entry.canvasEl) return;
      // Mark optimistically so two intersection callbacks don't double-render.
      entry.rendered = "in_flight";
      try {
        const pdfPage = await pdf.getPage(pageNum);
        const containerWidth =
          entry.wrapEl?.clientWidth || wrapRef.current?.clientWidth || 800;
        const baseViewport = pdfPage.getViewport({ scale: 1 });
        // Effective scale = fit-to-container × user zoom multiplier.
        // The fit factor keeps page-1 the same default size it always
        // was; the multiplier on top lets the toolbar's +/- controls
        // pan the entire pyramid up or down without re-flowing the
        // container width.
        const fitScale = containerWidth / baseViewport.width;
        const scale = fitScale * (zoom || 1);
        const viewport = pdfPage.getViewport({ scale });
        const dpr = window.devicePixelRatio || 1;
        const canvas = entry.canvasEl;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        // Lock the wrapper height to the true page height once we know it,
        // so the scrollbar settles at the real document height.
        if (entry.wrapEl) entry.wrapEl.style.height = `${viewport.height}px`;
        const task = pdfPage.render({ canvasContext: ctx, viewport });
        entry.renderTask = task;
        await task.promise;
        entry.rendered = true;
      } catch (e) {
        entry.rendered = false; // allow retry on next visibility
        if (e?.name !== "RenderingCancelledException") {
          onError?.(e);
        }
      }
    },
    [pdf, onError, zoom]
  );

  // When the user *changes* zoom after the document has loaded, invalidate
  // every cached canvas and immediately re-render any pages already on
  // screen. Skipped on initial mount so we don't race with the
  // IntersectionObserver-driven first paint (both would call
  // renderPage() on the same canvas → "Cannot use the same canvas
  // during multiple render() operations").
  const lastZoomRef = useRef(zoom);
  useEffect(() => {
    if (!pdf || numPages === 0) return undefined;
    if (lastZoomRef.current === zoom) return undefined;
    lastZoomRef.current = zoom;
    let aborted = false;
    (async () => {
      const visiblePages = [];
      for (const [n, entry] of Object.entries(pageRegistry.current)) {
        if (!entry) continue;
        if (entry.renderTask) {
          try { entry.renderTask.cancel(); } catch (_) {}
          try { await entry.renderTask.promise; } catch (_) {}
        }
        entry.renderTask = null;
        entry.rendered = false;
        const rect = entry.wrapEl?.getBoundingClientRect();
        if (rect && rect.bottom > 0 && rect.top < window.innerHeight * 2) {
          visiblePages.push(Number(n));
        }
      }
      if (aborted) return;
      visiblePages.forEach((n) => renderPage(n));
    })();
    return () => { aborted = true; };
  }, [zoom, pdf, numPages, renderPage]);

  // Set up two observers once the page wraps exist:
  //   1. Lazy render — render canvases approaching the viewport.
  //   2. Current page — whichever page has the most viewport coverage.
  useEffect(() => {
    if (!pdf || numPages === 0) return undefined;
    const root = null; // viewport
    const renderObs = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (en.isIntersecting) {
            const n = Number(en.target.dataset.page);
            if (n) renderPage(n);
          }
        }
      },
      { root, rootMargin: "200% 0px", threshold: 0 }
    );
    // Track current page — emit the one whose centre is closest to
    // the viewport centre.
    const currentObs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting && e.intersectionRatio > 0)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const n = Number(visible[0].target.dataset.page);
          if (n) onPageChange?.(n);
        }
      },
      { root, threshold: [0.1, 0.25, 0.5, 0.75] }
    );
    // Observe each wrap once it lands in the registry. Some wraps may
    // not yet have mounted; the ref callback below `observe()`s them
    // on first attach via a microtask. We also sweep here as a safety net.
    Object.values(pageRegistry.current).forEach((entry) => {
      if (entry.wrapEl) {
        renderObs.observe(entry.wrapEl);
        currentObs.observe(entry.wrapEl);
      }
    });
    return () => {
      renderObs.disconnect();
      currentObs.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, numPages, renderPage, onPageChange]);

  // Memoised range to avoid re-creating refs on unrelated re-renders.
  const pageList = useMemo(
    () => Array.from({ length: numPages }, (_, i) => i + 1),
    [numPages]
  );

  return (
    <div
      ref={wrapRef}
      className="pdf-canvas-wrap relative w-full bg-neutral-100 no-select"
      style={{ minHeight: "70vh" }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {numPages === 0 && (
        <div className="py-12 flex items-center justify-center label-mono text-neutral-500">
          — Loading PDF —
        </div>
      )}
      <div className="flex flex-col items-center gap-4 py-4">
        {pageList.map((n) => (
          <div
            key={n}
            data-page={n}
            data-testid={`pdf-page-${n}`}
            ref={(el) => {
              if (!el) return;
              const prev = pageRegistry.current[n] || {};
              pageRegistry.current[n] = { ...prev, wrapEl: el };
            }}
            className="pdf-page relative bg-white shadow-md"
            style={{
              // Placeholder size — uses page-1 aspect; the real canvas
              // overrides this once the page renders.
              width: "min(100%, 900px)",
              aspectRatio: `1 / ${aspect}`,
            }}
          >
            <canvas
              ref={(c) => {
                if (!c) return;
                const prev = pageRegistry.current[n] || {};
                pageRegistry.current[n] = { ...prev, canvasEl: c };
              }}
              data-testid={n === 1 ? "pdf-canvas" : undefined}
              className="block mx-auto"
              draggable={false}
            />
            <span className="absolute bottom-2 right-3 mono text-[10px] uppercase tracking-wider text-neutral-400 select-none">
              p. {n}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default PdfCanvasViewer;
